import { Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigService,
    ErrorUtilities,
    isDefined,
    LoadingScreenComponent,
    LoadingScreenModel,
    NotificationInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { combineLatest, defer, EMPTY, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    finalize,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { NotificationInstanceToken } from '../../core/interfaces/notification.interface';
import { FormsStorageService } from '../../services/forms-storage.service';
import { FormsWriteInstanceToken, FormsWriteInterface } from '../../services/forms-write.interface';
import { FormsService } from '../../services/forms.service';
import { FormsConstants } from '../../types/forms-constants';
import { FormListUtilities } from '../../utilities/form-list-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import { updateCustomerIdentification } from '../actions/customer-identification.actions';
import { emptyAction } from '../actions/empty.actions';
import {
    loadEquipmentSet,
    loadEquipmentSetError,
    loadEquipmentSetSuccess,
    loadFormList,
    loadFormListError,
    loadFormListSuccess,
} from '../actions/form-list.actions';
import {
    loadAllFormXml,
    loadForm,
    loadFormError,
    loadFormIntoCache,
    loadFormIntoCacheError,
    loadFormIntoCacheSuccess,
    loadFormSuccess,
    navigateToForm,
    updateSubmitStatus,
} from '../actions/form.actions';
import { updateLastOpenedForm } from '../actions/last-opened-form.actions';
import { getFormListEntryForNotificationFactory, getFormNotificationLists } from '../selectors/list.selector';
import { FormState } from '../state/form.state';
import { FormsState } from '../state/pace-forms.state';
import { loadSafetyAssessmentForm } from '../actions/safety-assessment.action';
import { getServiceActivityType } from '../selectors/pace-forms.selector';

// Effects for loading the forms list and the form(s)
@Injectable()
export class FormLoadEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store<FormsState>,
        private readonly formsService: FormsService,
        private readonly configService: ConfigService,
        private readonly appNotificationService: AppNotificationService,
        @Inject(NotificationInstanceToken) private readonly notificationService: NotificationInterface,
        private readonly modalService: BsModalService,
        @Inject(FormsWriteInstanceToken) private readonly formsWriteService: FormsWriteInterface,
        private readonly formsStorageService: FormsStorageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public loadForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadForm),
            switchMap((action) =>
                this.formsService
                    .getForm(action.form, action.notification, action.status, action.formType, action.language)
                    .pipe(
                        mergeMap((form: FormState) => {
                            // SPQ & SAF is not part of regular Forms list.
                            // So we should not update it to the LastOpenedForm State
                            if (
                                form.lockedBy === this.configService.getUserFrontId() &&
                                !FormUtilities.isServicePartQuestionnaire(form.type) &&
                                !FormUtilities.isSafetyAssessmentForm(
                                    form.formId.formId,
                                    this.configService.getSafetyAssessmentForm(),
                                )
                            ) {
                                return [
                                    loadFormSuccess({ form }),
                                    updateLastOpenedForm({ notification: action.notification, form: action.form }),
                                    updateSubmitStatus({
                                        form,
                                        notification: action.notification,
                                        submitStatus: FormsConstants.SUBMIT_STATUS.UNKNOWN,
                                    }),
                                ];
                            }

                            return [loadFormSuccess({ form })];
                        }),
                        catchError((error) => {
                            const errorMessage = ErrorUtilities.getErrorMessageFromStack(
                                JSON.stringify(error, Object.getOwnPropertyNames(error)),
                            );
                            const hasLock = errorMessage.indexOf(FormsConstants.HAS_LOCK) !== -1;

                            this.log.debug(
                                `FormEffects: loadForm$: Error in getForm, starting mitigation. User has lock: ${hasLock}`,
                            );

                            return [
                                loadFormError({
                                    error: errorMessage,
                                    hasLock,
                                    form: action.form,
                                    notification: action.notification,
                                }),
                            ];
                        }),
                    ),
            ),
            catchError((error) => {
                this.log.error(`FormEffects: loadForm$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public loadFormSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadFormSuccess),
            switchMap((action: ReturnType<typeof loadFormSuccess>) =>
                // We need to update the XML with the customer identification input field values that we got from the notification
                Object.values(
                    action.form.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].customerIdentification,
                )
                    .filter(isDefined)
                    .map((customerIdentificationInputField) => {
                        const newValue = customerIdentificationInputField.value
                            ? customerIdentificationInputField.value
                            : this.configService.getPlaceHolderForCustomerIdentification();

                        // SPQ does not contain Customer Identification section.
                        // So no need to dispatch updateCustomerIdentification() action
                        return FormUtilities.isServicePartQuestionnaire(action.form.type)
                            ? emptyAction()
                            : updateCustomerIdentification({
                                  xmlId: customerIdentificationInputField.xmlId,
                                  newValue,
                                  invalidateSignatures: false,
                              });
                    }),
            ),
            catchError((error) => {
                this.log.error(`FormEffects: loadFormSuccess$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public loadFormError$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadFormError),
                tap((action) => {
                    this.log.error(`FormEffects: loadFormError$: error in pipeline: ${action.error}`);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        `Error loading form ${action.form.formId}`,
                        `Unfortunately, there was an error while loading form '${action.form.formId}': <br><br>${action.error}<br><br>Please contact the support team and report this error if it persists.`,
                        () => {
                            // if we were unable to successfully load the form, we delete all local data and release
                            // the lock
                            // First display loading modal, then update local caches and release lock
                            const loadingScreenModel = new LoadingScreenModel('');
                            const modalInstance = this.modalService.show(LoadingScreenComponent, {
                                backdrop: 'static',
                                class: 'modal-sm',
                                initialState: {
                                    model: loadingScreenModel,
                                },
                                ariaLabelledBy: 'loading-screen-modal-title',
                            });

                            loadingScreenModel.loadingScreenDisplayed$
                                .pipe(
                                    take(1),
                                    mergeMap(() =>
                                        this.formsStorageService.removeFormStateFromCache(
                                            action.form,
                                            action.notification,
                                        ),
                                    ),
                                    mergeMap(() =>
                                        combineLatest([
                                            this.formsStorageService.removeLockFromCachedOdataList(
                                                action.form,
                                                action.notification,
                                            ),
                                            action.hasLock
                                                ? this.formsWriteService.releaseLock(action.form)
                                                : of(undefined),
                                        ]),
                                    ),
                                    catchError((cleanupError) => {
                                        this.log.error(
                                            `FormEffects: loadFormError$: error while cleaning up after erroneous form '${
                                                action.form.formId
                                            }': ${JSON.stringify(cleanupError)}`,
                                        );
                                        return of(undefined);
                                    }),
                                    finalize(() => modalInstance.hide()),
                                )
                                .subscribe();
                        },
                    );
                }),
            ),
        { dispatch: false },
    );

    public loadFormList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadFormList),
            withLatestFrom(this.store.select(getServiceActivityType)),
            mergeMap(([action, serviceActivity]) => {
                const notification = { ...action.notification };

                return this.formsService
                    .getFormList(
                        action.mainEquipment.matnr,
                        action.equipmentNr,
                        notification,
                        action.fromCache,
                        serviceActivity,
                    )
                    .pipe(
                        mergeMap((formList) => [
                            loadFormListSuccess({ formList }),
                            loadEquipmentSet({
                                notificationIdentifier: notification,
                                mainEquipment: action.mainEquipment,
                            }),
                        ]),
                        catchError((error) => {
                            this.log.error(`FormEffects: loadFormList$: error in pipeline: ${error}`);
                            return of(loadFormListError({ notification }));
                        }),
                    );
            }),
        ),
    );

    public loadSafetyAssesmentForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadSafetyAssessmentForm),
            mergeMap((action) => {
                const notification = { ...action.notification };
                const formId = { ...action.form };

                return of(
                    navigateToForm({
                        notification,
                        form: formId,
                        status: action.status,
                    }),
                );
            }),
        ),
    );

    public loadEquipmentSet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadEquipmentSet),
            filter(() => this.configService.getMatSerTypeaheadToggle()),
            mergeMap((action) => {
                const notificationIdentifier = action.notificationIdentifier;
                return this.formsService
                    .getEquipmentSetForNotification(action.notificationIdentifier, action.mainEquipment)
                    .pipe(
                        map((equipmentSet) => loadEquipmentSetSuccess({ notificationIdentifier, equipmentSet })),
                        catchError((error) => {
                            this.log.error(`FormEffects: loadEquipmentSet$: error in pipeline: ${error}`);
                            return of(loadEquipmentSetError({ notificationIdentifier }));
                        }),
                    );
            }),
        ),
    );

    public loadAllFormXml$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadAllFormXml),
            withLatestFrom(this.store.select(getFormNotificationLists)),
            switchMap(([action, lists]) => {
                const identifier = FormListUtilities.generateListIdentifierFromNotification(action.notification);
                const formList = lists[identifier];

                if (typeof formList === 'undefined') {
                    throw new Error(
                        `FormEffectsService: loadAllFormXml$: Could not load formList for identifier '${identifier}'`,
                    );
                }

                return formList.entries.map((entry) =>
                    loadFormIntoCache({
                        form: entry.id,
                        formType: entry.type,
                        notification: formList.notification,
                        status: action.status,
                    }),
                );
            }),
            catchError((error) => {
                this.log.error(`FormEffects: loadAllFormXml$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public loadFormIntoCache$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadFormIntoCache),
            mergeMap((action) => {
                return defer(() => {
                    const notification = this.notificationService.getNotificationStateByIdentifier(action.notification);
                    const language =
                        isDefined(notification) && notification.languageId.length > 0 ? notification.languageId : 'EN';
                    return this.formsService.cacheForm(
                        action.form,
                        action.notification,
                        action.formType,
                        language,
                        action.status,
                    );
                }).pipe(
                    map((odataXml) =>
                        loadFormIntoCacheSuccess({
                            form: action.form,
                            notification: action.notification,
                            lockedBy: odataXml.LockedBy,
                        }),
                    ),
                    catchError((error) => {
                        this.log.error(`FormEffects: loadFormIntoCache$: error in pipeline: ${JSON.stringify(error)}`);
                        return of(loadFormIntoCacheError());
                    }),
                );
            }),
            catchError((error) => {
                this.log.error(
                    `FormEffects: loadFormIntoCache$: FATAL error in pipeline, effects will not continue: ${JSON.stringify(
                        error,
                    )}`,
                );
                return of(loadFormIntoCacheError());
            }),
        ),
    );

    public updateSubmitStatus$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateSubmitStatus),
                concatMap((action) =>
                    this.store
                        .select(
                            getFormListEntryForNotificationFactory({
                                formId: action.form.formId,
                                notification: action.notification,
                            }),
                        )
                        .pipe(
                            filter(isDefined),
                            take(1),
                            filter(
                                (entry) =>
                                    entry.submitStatus === FormsConstants.SUBMIT_STATUS.ABORTED &&
                                    action.form.lockedBy === this.configService.getUserFrontId(),
                            ),
                            mergeMap(() =>
                                this.formsStorageService
                                    .saveFormSubmitStatus(action.notification, action.form.formId, action.submitStatus)
                                    .pipe(
                                        catchError((error) => {
                                            this.log.error(
                                                `FormEffects: updateSubmitStatus$: error in pipeline: ${JSON.stringify(error)}`,
                                            );
                                            return EMPTY;
                                        }),
                                    ),
                            ),
                        ),
                ),
            ),
        { dispatch: false },
    );
}
